import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
    ArrowLeft,
    Save,
    Car,
    Home,
    MapPin,
    Tag,
    Image as ImageIcon,
    X,
    Plus,
    ChevronDown,
    Upload,
    Trash2,
} from 'lucide-react';
import {
    useGetDealerListingQuery,
    useUpdateDealerListingMutation,
    UpdateDealerListingRequest,
} from '../../store/slices/apiSlice';
import { toast } from 'react-hot-toast';
import { Button } from '../../components/ui/Button';

interface LocationSuggestion {
    description: string;
    place_id: string;
    structured_formatting: {
        main_text: string;
        secondary_text: string;
    };
}

const currencySymbols = [
    { symbol: '₦', code: 'NGN', name: 'Nigerian Naira' },
    { symbol: '$', code: 'USD', name: 'US Dollar' },
    { symbol: '€', code: 'EUR', name: 'Euro' },
    { symbol: '£', code: 'GBP', name: 'British Pound' },
    { symbol: '¥', code: 'JPY', name: 'Japanese Yen' },
    { symbol: '₹', code: 'INR', name: 'Indian Rupee' },
];

const DealerEditListing: React.FC = () => {
    const navigate = useNavigate();
    const { listing_id } = useParams<{ listing_id: string }>();
    const listingId = listing_id ? parseInt(listing_id) : null;
    const { token } = useSelector((state: RootState) => state.auth);

    const { data: listingResponse, isLoading, error } = useGetDealerListingQuery(listingId!, {
        skip: !listingId || !token,
    });
    const [updateListing, { isLoading: isSubmitting }] = useUpdateDealerListingMutation();

    const listing = listingResponse?.data;

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        price: 0,
        currency_code: 'NGN',
        currency_symbol: '₦',
        location: '',
        tags: [] as string[],
        car_details: {} as any,
        house_details: {} as any,
    });

    const [existingImages, setExistingImages] = useState<Array<{ image_id: number; image_path: string; is_primary: boolean }>>([]);
    const [imagesToRemove, setImagesToRemove] = useState<number[]>([]);
    const [newImages, setNewImages] = useState<File[]>([]);
    const [imagePreviews, setImagePreviews] = useState<string[]>([]);
    const [newTag, setNewTag] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    
    const [locationSearch, setLocationSearch] = useState('');
    const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
    const [locationSuggestions, setLocationSuggestions] = useState<LocationSuggestion[]>([]);
    const [isSearchingLocation, setIsSearchingLocation] = useState(false);
    const locationRef = useRef<HTMLDivElement>(null);
    
    const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
    const currencyRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (listing) {
            setFormData({
                title: listing.title,
                description: listing.description,
                price: listing.price,
                currency_code: listing.currency_code || 'NGN',
                currency_symbol: listing.currency_symbol || '₦',
                location: listing.location || '',
                tags: listing.tags || [],
                car_details: listing.car_details || {},
                house_details: listing.house_details || {},
            });
            setExistingImages(listing.images || []);
            if (listing.location) {
                setLocationSearch(listing.location);
            }
        }
    }, [listing]);

    const handleInputChange = (field: string, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleCarDetailChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            car_details: {
                ...prev.car_details,
                [field]: field === 'year' || field === 'mileage' ? (value ? Number(value) : undefined) : value
            }
        }));
    };

    const handleHouseDetailChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            house_details: {
                ...prev.house_details,
                [field]: field === 'bedrooms' || field === 'bathrooms' || field === 'area' ? (value ? Number(value) : undefined) : value
            }
        }));
    };

    const handleAddTag = () => {
        if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
            handleInputChange('tags', [...formData.tags, newTag.trim()]);
            setNewTag('');
        }
    };

    const handleRemoveTag = (tagToRemove: string) => {
        handleInputChange('tags', formData.tags.filter(tag => tag !== tagToRemove));
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files);
            const newPreviews = newFiles.map(file => URL.createObjectURL(file));
            setNewImages(prev => [...prev, ...newFiles]);
            setImagePreviews(prev => [...prev, ...newPreviews]);
        }
    };

    const removeNewImage = (index: number) => {
        setNewImages(prev => prev.filter((_, i) => i !== index));
        setImagePreviews(prev => {
            URL.revokeObjectURL(prev[index]);
            return prev.filter((_, i) => i !== index);
        });
    };

    const removeExistingImage = (imageId: number) => {
        setImagesToRemove(prev => [...prev, imageId]);
        setExistingImages(prev => prev.filter(img => img.image_id !== imageId));
    };

    const searchLocation = async (query: string) => {
        if (!query || query.length < 3) {
            setLocationSuggestions([]);
            return;
        }

        setIsSearchingLocation(true);
        try {
            const response = await fetch(
                `https://maps.googleapis.com/maps/api/place/autocomplete/json?input=${encodeURIComponent(query)}&key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''}`
            );
            const data = await response.json();
            if (data.predictions) {
                setLocationSuggestions(data.predictions);
                setShowLocationSuggestions(true);
            }
        } catch (error) {
            console.error('Location search error:', error);
        } finally {
            setIsSearchingLocation(false);
        }
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (locationSearch) {
                searchLocation(locationSearch);
            } else {
                setLocationSuggestions([]);
                setShowLocationSuggestions(false);
            }
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [locationSearch]);

    const selectLocation = (location: LocationSuggestion) => {
        setLocationSearch(location.description);
        handleInputChange('location', location.description);
        setShowLocationSuggestions(false);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (locationRef.current && !locationRef.current.contains(event.target as Node)) {
                setShowLocationSuggestions(false);
            }
            if (currencyRef.current && !currencyRef.current.contains(event.target as Node)) {
                setShowCurrencyDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectCurrencySymbol = (currency: typeof currencySymbols[0]) => {
        handleInputChange('currency_symbol', currency.symbol);
        handleInputChange('currency_code', currency.code);
        setShowCurrencyDropdown(false);
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }
        if (!formData.description.trim()) {
            newErrors.description = 'Description is required';
        }
        if (!formData.price || formData.price <= 0) {
            newErrors.price = 'Price is required and must be greater than 0';
        }

        if (listing?.listing_type === 'car' && formData.car_details?.vin) {
            const vinRegex = /^[A-HJ-NPR-Z0-9]{17}$/;
            if (!vinRegex.test(formData.car_details.vin)) {
                newErrors.vin = 'VIN must be exactly 17 alphanumeric characters';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Please fix the errors in the form');
            return;
        }

        try {
            const formDataToSend = new FormData();
            
            formDataToSend.append('listing_id', listingId!.toString());
            formDataToSend.append('title', formData.title);
            formDataToSend.append('description', formData.description);
            formDataToSend.append('price', formData.price.toString());
            if (formData.currency_code) formDataToSend.append('currency_code', formData.currency_code);
            if (formData.currency_symbol) formDataToSend.append('currency_symbol', formData.currency_symbol);
            if (formData.location) formDataToSend.append('location', formData.location);
            
            // Always send tags as an array - backend requires tags to be an array
            // Use tags[] format when tags exist, or send empty array as JSON string
            const tagsToSend = Array.isArray(formData.tags) ? formData.tags.filter(tag => tag && tag.trim()) : [];
            if (tagsToSend.length > 0) {
                // Send as tags[] entries (FormData array format)
                tagsToSend.forEach(tag => {
                    formDataToSend.append('tags[]', tag.trim());
                });
            } else {
                // Send empty array as JSON string to ensure backend receives an array
                formDataToSend.append('tags', JSON.stringify([]));
            }

            // Always send car_details as an object when listing type is 'car'
            // Use bracket notation so backend receives it as an object structure
            if (listing?.listing_type === 'car') {
                const carDetails = formData.car_details || {};
                // Always send car_details fields, even if empty, to ensure backend receives an object
                const carFields = ['vin', 'make', 'model', 'year', 'mileage'];
                let hasAnyValue = false;
                carFields.forEach(carKey => {
                    const value = carDetails[carKey];
                    if (value !== undefined && value !== null && value !== '') {
                        formDataToSend.append(`car_details[${carKey}]`, String(value));
                        hasAnyValue = true;
                    }
                });
                // If no values exist, send at least one empty field to establish object structure
                if (!hasAnyValue) {
                    formDataToSend.append('car_details[vin]', '');
                }
            }

            // Always send house_details as an object when listing type is 'house'
            // Use bracket notation so backend receives it as an object structure
            if (listing?.listing_type === 'house') {
                const houseDetails = formData.house_details || {};
                // Always send house_details fields, even if empty, to ensure backend receives an object
                const houseFields = ['bedrooms', 'bathrooms', 'area'];
                let hasAnyValue = false;
                houseFields.forEach(houseKey => {
                    const value = houseDetails[houseKey];
                    if (value !== undefined && value !== null && value !== '') {
                        formDataToSend.append(`house_details[${houseKey}]`, String(value));
                        hasAnyValue = true;
                    }
                });
                // If no values exist, send at least one empty field to establish object structure
                if (!hasAnyValue) {
                    formDataToSend.append('house_details[bedrooms]', '');
                }
            }

            // Send remove_image_ids as an array format
            // Backend expects remove_image_ids[] format for arrays in FormData
            if (imagesToRemove.length > 0) {
                imagesToRemove.forEach((id: number) => {
                    formDataToSend.append('remove_image_ids[]', String(id));
                });
            }

            newImages.forEach(image => {
                formDataToSend.append('images', image);
            });

            const response = await updateListing(formDataToSend).unwrap();
            
            if (response.success) {
                toast.success('Listing updated successfully! Waiting for admin approval.');
                navigate('/dealer/listings');
            }
        } catch (error: any) {
            console.error('Failed to update listing:', error);
            toast.error(error?.data?.message || 'Failed to update listing');
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error || !listing) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-red-600 text-sm">Failed to load listing</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="bg-white border-b border-gray-200">
                <div className="px-6 py-4">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => navigate('/dealer/listings')}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Edit Listing</h1>
                            <p className="text-gray-600">Update your {listing.listing_type} listing</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-6 py-6">
                <div className="max-w-4xl mx-auto">
                    <form onSubmit={handleSubmit} className="space-y-8">
                        {/* Basic Information */}
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
                            </div>
                            <div className="p-6 space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => handleInputChange('title', e.target.value)}
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                                            errors.title ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    />
                                    {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => handleInputChange('description', e.target.value)}
                                        rows={4}
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                                            errors.description ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    />
                                    {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Price *</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-700 font-semibold text-lg">
                                                {formData.currency_symbol}
                                            </span>
                                            <input
                                                type="number"
                                                value={formData.price}
                                                onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                                                className={`w-full pl-16 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                                                    errors.price ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            />
                                        </div>
                                        {errors.price && <p className="mt-1 text-sm text-red-600">{errors.price}</p>}
                                    </div>
                                    <div ref={currencyRef} className="relative">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                                        <button
                                            type="button"
                                            onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between"
                                        >
                                            <span>{formData.currency_symbol}</span>
                                            <ChevronDown className="w-4 h-4" />
                                        </button>
                                        {showCurrencyDropdown && (
                                            <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                                {currencySymbols.map((currency, index) => (
                                                    <button
                                                        key={index}
                                                        type="button"
                                                        onClick={() => selectCurrencySymbol(currency)}
                                                        className="w-full text-left px-4 py-3 hover:bg-gray-50"
                                                    >
                                                        {currency.symbol} - {currency.name}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div ref={locationRef}>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                                    <div className="relative">
                                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                        <input
                                            type="text"
                                            value={locationSearch}
                                            onChange={(e) => {
                                                setLocationSearch(e.target.value);
                                                handleInputChange('location', e.target.value);
                                            }}
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        />
                                        {showLocationSuggestions && locationSuggestions.length > 0 && (
                                            <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                                {locationSuggestions.map((location, index) => (
                                                    <button
                                                        key={index}
                                                        type="button"
                                                        onClick={() => selectLocation(location)}
                                                        className="w-full text-left px-4 py-3 hover:bg-gray-50"
                                                    >
                                                        {location.structured_formatting?.main_text || location.description}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                                    <div className="flex flex-wrap gap-2 mb-2">
                                        {formData.tags.map((tag, index) => (
                                            <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                                                {tag}
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveTag(tag)}
                                                    className="ml-2"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={newTag}
                                            onChange={(e) => setNewTag(e.target.value)}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleAddTag();
                                                }
                                            }}
                                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                                            placeholder="Add tag and press Enter"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddTag}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg"
                                        >
                                            <Plus className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Car Details */}
                        {listing.listing_type === 'car' && (
                            <div className="bg-white rounded-lg shadow">
                                <div className="px-6 py-4 border-b border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                        <Car className="w-5 h-5 mr-2" />
                                        Car Details
                                    </h2>
                                </div>
                                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">VIN Number</label>
                                        <input
                                            type="text"
                                            value={formData.car_details?.vin || ''}
                                            onChange={(e) => handleCarDetailChange('vin', e.target.value.toUpperCase())}
                                            maxLength={17}
                                            className={`w-full px-4 py-2 border rounded-lg ${
                                                errors.vin ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                        />
                                        {errors.vin && <p className="mt-1 text-sm text-red-600">{errors.vin}</p>}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Make</label>
                                        <input
                                            type="text"
                                            value={formData.car_details?.make || ''}
                                            onChange={(e) => handleCarDetailChange('make', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                                        <input
                                            type="text"
                                            value={formData.car_details?.model || ''}
                                            onChange={(e) => handleCarDetailChange('model', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                                        <input
                                            type="number"
                                            value={formData.car_details?.year || ''}
                                            onChange={(e) => handleCarDetailChange('year', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Mileage</label>
                                        <input
                                            type="number"
                                            value={formData.car_details?.mileage || ''}
                                            onChange={(e) => handleCarDetailChange('mileage', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* House Details */}
                        {listing.listing_type === 'house' && (
                            <div className="bg-white rounded-lg shadow">
                                <div className="px-6 py-4 border-b border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                        <Home className="w-5 h-5 mr-2" />
                                        House Details
                                    </h2>
                                </div>
                                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Bedrooms</label>
                                        <input
                                            type="number"
                                            value={formData.house_details?.bedrooms || ''}
                                            onChange={(e) => handleHouseDetailChange('bedrooms', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Bathrooms</label>
                                        <input
                                            type="number"
                                            value={formData.house_details?.bathrooms || ''}
                                            onChange={(e) => handleHouseDetailChange('bathrooms', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Area (sq ft)</label>
                                        <input
                                            type="number"
                                            value={formData.house_details?.area || ''}
                                            onChange={(e) => handleHouseDetailChange('area', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Images */}
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <ImageIcon className="w-5 h-5 mr-2" />
                                    Images
                                </h2>
                            </div>
                            <div className="p-6">
                                {/* Existing Images */}
                                {existingImages.length > 0 && (
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                        {existingImages.map((image) => (
                                            <div key={image.image_id} className="relative group">
                                                <img
                                                    src={image.image_path}
                                                    alt="Listing"
                                                    className="w-full h-32 object-cover rounded-lg border border-gray-300"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removeExistingImage(image.image_id)}
                                                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                                {image.is_primary && (
                                                    <span className="absolute bottom-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                                                        Primary
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* New Image Previews */}
                                {imagePreviews.length > 0 && (
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                        {imagePreviews.map((preview, index) => (
                                            <div key={index} className="relative group">
                                                <img
                                                    src={preview}
                                                    alt={`New ${index + 1}`}
                                                    className="w-full h-32 object-cover rounded-lg border border-gray-300"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removeNewImage(index)}
                                                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors bg-gray-50">
                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                        <Upload className="w-8 h-8 text-gray-400 mb-2" />
                                        <p className="text-sm text-gray-600">
                                            <span className="font-semibold">Click to upload</span> or drag and drop
                                        </p>
                                    </div>
                                    <input
                                        type="file"
                                        className="hidden"
                                        accept="image/*"
                                        multiple
                                        onChange={handleImageChange}
                                    />
                                </label>
                            </div>
                        </div>

                        {/* Submit */}
                        <div className="flex justify-end gap-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate('/dealer/listings')}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        <span>Updating...</span>
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4" />
                                        <span>Update Listing</span>
                                    </>
                                )}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default DealerEditListing;
